'use client';

import { useCallback, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  showWarningIcon?: boolean;
  leadingIcon?: ReactNode;
};

type SelectSize = 'sm' | 'xs';
type SelectVariant = 'bordered' | 'ghost' | 'unstyled';
type SelectOptionsPlacement = 'auto' | 'bottom' | 'top';
type SelectOptionsAlign = 'start' | 'end';
type ResolvedSelectOptionsPlacement = Exclude<SelectOptionsPlacement, 'auto'>;

type SelectProps = {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  size?: SelectSize;
  fullWidth?: boolean;
  variant?: SelectVariant;
  className?: string;
  buttonClassName?: string;
  fitContent?: boolean;
  hideChevron?: boolean;
  showSuffixIndicator?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  ariaLabel?: string;
  optionsPlacement?: SelectOptionsPlacement;
  optionsClassName?: string;
  optionsAlign?: SelectOptionsAlign;
  hideSelectedLabel?: boolean;
  // 트리거(선택된 값)의 leading 아이콘 override. 지정 시 selectedOption.leadingIcon 대신 사용한다.
  // 현재 value가 options에 없어도(예: 선택 불가한 상태) 트리거 글리프를 그리기 위한 용도.
  selectedLeadingIcon?: ReactNode;
  portal?: boolean;
};

const sizeConfig: Record<
  SelectSize,
  { button: string; option: string; icon: string; iconRight: string; minWidth: string }
> = {
  sm: {
    button: 'h-8 min-h-8 px-3 pr-7 text-sm',
    option: 'px-3 py-1.5 text-sm',
    icon: 'h-3.5 w-3.5',
    iconRight: 'right-2',
    minWidth: 'min-w-28',
  },
  xs: {
    button: 'h-6 min-h-6 px-2 pr-6 text-xs',
    option: 'px-2 py-1 text-xs',
    icon: 'h-3 w-3',
    iconRight: 'right-1.5',
    minWidth: 'min-w-20',
  },
};

const defaultSizeConfig = {
  button: 'h-8 min-h-8 px-4 pr-8 text-sm',
  option: 'px-4 py-2 text-sm',
  icon: 'h-4 w-4',
  iconRight: 'right-2.5',
  minWidth: 'min-w-36',
};

const optionsGap = 4;
const viewportPadding = 8;
const defaultMaxOptionsHeight = 240;
const minOptionsHeight = 48;

const getEstimatedOptionHeight = (size?: SelectSize) => {
  if (size === 'xs') return 24;
  if (size === 'sm') return 32;
  return 40;
};

const getViewportHeight = () => window.visualViewport?.height ?? window.innerHeight;

export const Select = ({
  options,
  value,
  onChange,
  size,
  fullWidth = false,
  variant = 'bordered',
  className,
  buttonClassName,
  fitContent = false,
  hideChevron = false,
  showSuffixIndicator = false,
  disabled = false,
  id,
  name,
  ariaLabel,
  optionsPlacement = 'auto',
  optionsClassName,
  optionsAlign = 'start',
  hideSelectedLabel = false,
  selectedLeadingIcon,
  portal = true,
}: SelectProps) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [autoPlacement, setAutoPlacement] = useState<ResolvedSelectOptionsPlacement>('bottom');
  const [maxOptionsHeight, setMaxOptionsHeight] = useState(defaultMaxOptionsHeight);
  const config = size ? sizeConfig[size] : defaultSizeConfig;
  const selectedOption = options.find((opt) => opt.value === value);
  // value가 options에 없어도(예: 편집 대상 밖 상태) 트리거 글리프를 잃지 않도록 override를 우선한다.
  const triggerLeadingIcon = selectedLeadingIcon ?? selectedOption?.leadingIcon;

  const borderClass =
    variant === 'bordered'
      ? 'border border-base-content/20 focus:border-primary focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary/20'
      : variant === 'ghost'
        ? 'border border-transparent bg-transparent'
        : '';

  const wrapperWidth = fullWidth ? 'w-full' : fitContent ? 'inline-flex w-auto' : `inline-flex ${config.minWidth}`;

  const updateOptionsLayout = useCallback(() => {
    const button = buttonRef.current;
    if (!button || typeof window === 'undefined') {
      return;
    }

    const rect = button.getBoundingClientRect();
    const viewportHeight = getViewportHeight();
    const spaceAbove = Math.max(0, rect.top - viewportPadding - optionsGap);
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom - viewportPadding - optionsGap);
    const preferredHeight = Math.min(
      defaultMaxOptionsHeight,
      Math.max(minOptionsHeight, options.length * getEstimatedOptionHeight(size) + 8),
    );
    const nextPlacement =
      optionsPlacement === 'auto'
        ? spaceBelow < preferredHeight && spaceAbove > spaceBelow
          ? 'top'
          : 'bottom'
        : optionsPlacement;
    const availableSpace = nextPlacement === 'top' ? spaceAbove : spaceBelow;

    setAutoPlacement(nextPlacement);
    setMaxOptionsHeight(Math.max(minOptionsHeight, Math.min(defaultMaxOptionsHeight, availableSpace)));
  }, [options.length, optionsPlacement, size]);

  const resolvedOptionsPlacement = optionsPlacement === 'auto' ? autoPlacement : optionsPlacement;
  const optionsPositionClass = resolvedOptionsPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1';
  const optionsAlignClass = optionsAlign === 'end' ? 'right-0' : 'left-0';
  const anchorPosition = `${resolvedOptionsPlacement} ${optionsAlign === 'end' ? 'end' : 'start'}` as
    | 'bottom start'
    | 'bottom end'
    | 'top start'
    | 'top end';
  const optionsStyle = {
    '--anchor-max-height': `${maxOptionsHeight}px`,
    '--select-options-max-height': `${maxOptionsHeight}px`,
  } as CSSProperties;

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled} name={name}>
      <div className={`group relative ${wrapperWidth} ${className ?? ''}`}>
        <ListboxButton
          ref={buttonRef}
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          value={value}
          onMouseDown={updateOptionsLayout}
          onTouchStart={updateOptionsLayout}
          onClick={updateOptionsLayout}
          onFocus={updateOptionsLayout}
          onKeyDown={(event) => {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
              updateOptionsLayout();
            }
          }}
          onChange={(event) => {
            const nextValue = (event.target as HTMLButtonElement).value;
            if (!nextValue || nextValue === value) {
              return;
            }
            if (options.some((option) => option.value === nextValue && !option.disabled)) {
              onChange(nextValue);
            }
          }}
          className={[
            'relative w-full cursor-pointer text-left text-base-content',
            'transition-colors duration-200',
            'disabled:cursor-not-allowed disabled:opacity-50',
            variant === 'unstyled' ? '' : 'rounded-field bg-base-100 hover:border-base-content/40',
            variant === 'unstyled' ? '' : config.button,
            borderClass,
            buttonClassName ?? '',
          ].join(' ')}
        >
          <span className="inline-flex min-w-0 max-w-full items-center gap-1">
            {triggerLeadingIcon ? (
              <span className="shrink-0 text-[var(--color-fg-muted)]">{triggerLeadingIcon}</span>
            ) : null}
            {hideSelectedLabel ? null : (
              <span className="block min-w-0 flex-1 truncate">{selectedOption?.label ?? value}</span>
            )}
            {selectedOption?.showWarningIcon ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className="h-3.5 w-3.5 shrink-0 text-warning"
                aria-label="warning"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.8"
                  d="M12 9v4m0 3h.01M10.29 3.86l-7.2 12.47A2 2 0 004.82 19h14.36a2 2 0 001.73-2.67l-7.2-12.47a2 2 0 00-3.46 0z"
                />
              </svg>
            ) : null}
            {showSuffixIndicator ? (
              <span className="text-[10px] leading-none text-[var(--color-fg-muted)]">▾</span>
            ) : null}
          </span>
          {!hideChevron ? (
            <span className={`pointer-events-none absolute inset-y-0 ${config.iconRight} flex items-center`}>
              <svg
                className={`${config.icon} text-[var(--color-fg-muted)] transition-colors duration-200 group-hover:text-primary`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 8l4 4 4-4" />
              </svg>
            </span>
          ) : null}
        </ListboxButton>

        <ListboxOptions
          portal={portal}
          anchor={portal ? { to: anchorPosition, gap: optionsGap, padding: viewportPadding } : undefined}
          style={optionsStyle}
          className={[
            portal
              ? 'z-[1040] w-[var(--button-width)] min-w-[var(--button-width)] overflow-auto rounded-field bg-base-100 py-1 shadow-lg'
              : `absolute ${optionsAlignClass} ${optionsPositionClass} z-50 w-full overflow-auto rounded-field bg-base-100 py-1 shadow-lg`,
            'border border-base-content/10',
            'max-h-[var(--select-options-max-height)] focus:outline-none',
            optionsClassName ?? '',
          ].join(' ')}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={[
                'relative cursor-pointer select-none',
                config.option,
                'text-base-content',
                'data-[focus]:bg-base-200/80 data-[focus]:text-base-content',
                'data-[selected]:font-semibold',
                'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
              ].join(' ')}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                  {option.leadingIcon ? (
                    <span className="shrink-0 text-[var(--color-fg-muted)]">{option.leadingIcon}</span>
                  ) : null}
                  <span className="block truncate">{option.label}</span>
                </span>
                {option.showWarningIcon ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="h-3.5 w-3.5 shrink-0 text-warning"
                    aria-label="warning"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.8"
                      d="M12 9v4m0 3h.01M10.29 3.86l-7.2 12.47A2 2 0 004.82 19h14.36a2 2 0 001.73-2.67l-7.2-12.47a2 2 0 00-3.46 0z"
                    />
                  </svg>
                ) : null}
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
